import classNames from "./Survey.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLogEvent } from "../../hooks/log";
import { IoStar, IoStarOutline } from "react-icons/io5";
import { useRoomId } from "../context/room/room";
import api, { Recording } from "../../hooks/api-new";
import { useAxios } from "../../hooks/axios";
import Loader from "./Loader";
import { useNavigate } from "react-router-dom";
import Button from "./Button";
import { toast } from "react-toastify";
import { FaLinkedin, FaReddit } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

interface StarsProps {
  rating: number;
  setRating: (stars: number) => void;
}

function Stars({ rating, setRating }: StarsProps) {
  const logEvent = useLogEvent();
  const roomId = useRoomId();
  const stars: string[] = useMemo(() => {
    return rating.toString().split(".");
  }, [rating]);
  const numWholeStars = useMemo(() => {
    return Number(stars[0]);
  }, [stars]);

  const onClickRating = useCallback(
    (newRating: number) => {
      setRating(newRating);
      logEvent({
        type: "rating",
        details: { rating: newRating, room_id: roomId },
      });
    },
    [logEvent, roomId, setRating]
  );

  const wholeStar = Array.from(Array(numWholeStars), (_, index) => (
    <IoStar
      style={{ color: "var(--langfinity-yellow)" }}
      onClick={() => onClickRating(index + 1)}
      key={index}
    />
  ));

  const emptyStars = Array.from(Array(5 - numWholeStars), (_, index) => (
    <IoStarOutline
      onClick={() => onClickRating(index + 1 + numWholeStars)}
      key={index}
    />
  ));

  return (
    <div
      className={`${classNames.FeedbackStars} ${
        rating > 0 ? classNames.Small : ""
      }`}
    >
      {wholeStar}
      {emptyStars}
    </div>
  );
}

function Survey() {
  const [rating, setRating] = useState(0);
  const [emails, setEmails] = useState("");
  const axios = useAxios();
  const submitReferrals = api.useSubmitReferrals(axios);
  const roomId = useRoomId();
  const [recording, setRecording] = useState<Recording>();
  const [loading, setLoading] = useState(false);
  const getRecordingList = api.useGetRecordingList(axios);
  const navigate = useNavigate();
  const logEvent = useLogEvent();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const recordingsResponse = await getRecordingList();
        setRecording(recordingsResponse.data.recordingList[0]);
      } finally {
        setLoading(false);
      }
    })();
  }, [getRecordingList]);

  const handleClick = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onSubmitEmails = () => {
    logEvent({ type: "refer_emails", details: { emails } });
    submitReferrals({ emails, roomId });
    setEmails("");
    toast(
      <div className="Toast SuccessToast">
        <div>{"Emails submitted successfully"}</div>
      </div>,
      { type: "success" }
    );
  };

  const shouldShowRecording = (
    recording: Recording | undefined,
    roomId: string
  ): boolean => {
    // Early return if basic conditions aren't met
    if (!recording) {
      return false;
    }
    // Check if room IDs match (first 6 characters)
    const roomIdMatches = recording.room_id?.substring(0, 6) === roomId;
    if (!roomIdMatches) {
      return false;
    }
    // If recording hasn't stopped, show it
    if (!recording.stopped_at) {
      return true;
    }
    // Check if recording stopped less than 10 minutes ago
    const stoppedAt = new Date(recording.stopped_at);
    const now = new Date();
    const timeDifferenceInMinutes =
      (now.getTime() - stoppedAt.getTime()) / (1000 * 60);

    return timeDifferenceInMinutes < 10;
  };

  if (loading) return <Loader />;

  return (
    <div className={classNames.Survey}>
      <div className={classNames.RecordingSection}>
        <h1>Real-time translation ended</h1>
        {shouldShowRecording(recording, roomId) && (
          <>
            <p style={{ color: "var(--langfinity-medium-gray)" }}>
              View recording in multiple languages
            </p>
            <Button onClick={() => navigate(`/recording/${recording!.id}`)}>
              View recording
            </Button>
          </>
        )}
      </div>

      <div className={classNames.BottomSection}>
        <div className={classNames.Ratings}>
          <div>
            <p>Thank you for using Langfinity!</p>
            <p>How would you rate your experience?</p>
          </div>
          <Stars rating={rating} setRating={setRating} />
          <div className={classNames.AfterRating}>
            {rating >= 4 && (
              <div>
                <p
                  style={{
                    color: "var(--langfinity-medium-gray)",
                    fontSize: 14,
                  }}
                >
                  Share your real-time translation experience with the world 🌎
                </p>
                <div className={classNames.Socials}>
                  <button
                    onClick={() => {
                      logEvent({ type: "click_share_reddit" });
                      handleClick(
                        "https://reddit.com/submit?url=https%3A%2F%2Flangfinity.ai&title=Just%20experienced%20real-time%20translation%20in%2050%2B%20languages%20with%20Langfinity!%20%F0%9F%8C%8F%20Amazing%20how%20AI%20can%20break%20down%20language%20barriers%20in%20meetings%20and%20events."
                      );
                    }}
                  >
                    <FaReddit />
                  </button>
                  <button
                    onClick={() => {
                      logEvent({ type: "click_share_x" });
                      handleClick(
                        `https://x.com/intent/tweet?text=Just+experienced+real-time+translation+in+50%2B+languages+with+%40Langfinity%21+%F0%9F%8C%8F+Amazing+how+AI+can+break+down+language+barriers+in+meetings+and+events.+Try+it+here%3A+langfinity.ai+%23AI+%23Translation+%23Innovation`
                      );
                    }}
                  >
                    <FaXTwitter />
                  </button>
                  <button
                    onClick={() => {
                      logEvent({ type: "click_share_linkedin" });
                      handleClick(
                        `http://www.linkedin.com/shareArticle?mini=true&url=https://langfinity.ai`
                      );
                    }}
                  >
                    <FaLinkedin />
                  </button>
                </div>
              </div>
            )}
            {rating > 0 && rating < 4 && (
              <div>
                <Button
                  onClick={() =>
                    handleClick("https://forms.gle/JDLEdUVJviHPSm7o9")
                  }
                >
                  Take me to the feedback form
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className={classNames.Referrals}>
          <p>Refer us and earn 10 FREE hours when your referral joins!</p>
          <div>
            <input
              type="email"
              placeholder="Enter your referral's work email here"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              className={classNames.EmailInput}
            />
            <Button
              onClick={onSubmitEmails}
              color="gray"
              backgroundColor="black"
              borderColor="dark-gray"
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Survey;
